import {AdditionalRequests, CashSimulationDemand, SimulationDemand} from '@/models/Demand';
import Axios, {CancelTokenSource} from 'axios';
import {ProductType} from '@/commons/enums/ProductType';
import {Offer, Simulation, SimulationPostDTO} from '@/models/simulation/Simulation';
import {cloneDeep} from 'lodash-es';
import EnvironmentService from '@/env/EnvironmentService';
import {FpAxios} from 'fp-components';
import {
  ScheduleParams,
  Schedule,
  HomeStartProgram,
  HomeStartProgramInput
} from '@/components/calculator/schedule/Schedule';
import processProxy from '@/env/processProxy';

const SIMULATION_V2_PREFIX: string = 'v2/';

class SimulationApi {
  private simulationAxios = Axios.create({
    headers: {
      ...(!EnvironmentService.Environment.isDev && { 'X-App-Hash': processProxy.VUE_APP_COMMIT_HASH, }),
    },
  });

  private simulationRequests: CancelTokenSource[] = [];
  private pdfAxios = Axios.create();
  private dealsAxios = FpAxios.create();

  private getSimulationTypePrefix(productType: ProductType): string {
    switch (productType) {
    case ProductType.CASH:
      return 'cash-';
    case ProductType.SME:
      return 'sme-';
    default:
      return '';
    }
  }

  private getSimulationVersion = (type: ProductType) =>
    EnvironmentService.Environment.isBackendSimV2 && type === ProductType.MORTGAGE ? SIMULATION_V2_PREFIX : '';

  private simulationPrefix(productType: ProductType): string {
    return `${processProxy.VUE_APP_SIMULATION_URL}${this.getSimulationTypePrefix(productType)}`;
  }

  private prepareUserInput(userInput: SimulationDemand): SimulationDemand {
    const data = cloneDeep(userInput);
    if (data.households && data.households.length === 0) {
      delete data.households;
    }
    return data;
  }

  public async getSimulation(simulationId: string, productType: ProductType): Promise<Simulation> {
    const response = await this.simulationAxios.get(`${this.simulationPrefix(productType)}backend/api/${this.getSimulationVersion(productType)}simulation/${simulationId}`);
    return new Simulation(response.data);
  }

  public async postSimulation(id: string, productType: ProductType, userInput: SimulationDemand): Promise<Simulation> {
    const response = await this.simulationAxios.post<Simulation>(`${this.simulationPrefix(productType)}backend/api/simulation/${id}`, this.prepareUserInput(userInput));
    return response.data;
  }

  public async createCashSimulation(loanAmount: number): Promise<string> {
    const userInput: CashSimulationDemand = new CashSimulationDemand({loanAmount,});
    return this.createNewSimulation(ProductType.CASH, userInput);
  }

  public async createNewSimulation(productType: ProductType, userInput: SimulationDemand): Promise<string> {
    const response = await this.simulationAxios.post(`${this.simulationPrefix(productType)}backend/api/simulation`, this.prepareUserInput(userInput));
    return response.data.id;
  }

  public async createNewSimulationV2(productType: ProductType, userInput: SimulationDemand): Promise<string> {
    const response = await this.simulationAxios.post(`${this.simulationPrefix(productType)}backend/api/${this.getSimulationVersion(productType)}simulation`, this.prepareUserInput(userInput));
    return response.data.id;
  }

  public async updateSimulationV2(
    id: string,
    type: ProductType,
    userInput: SimulationDemand): Promise<SimulationPostDTO> {
    const response = await this.simulationAxios.post<SimulationPostDTO>(`${this.simulationPrefix(type)}backend/api/${this.getSimulationVersion(type)}simulation/${id}`, this.prepareUserInput(userInput));
    return response.data;
  }

  public async updatePersistentSimulationV2(
    id: string,
    type: ProductType,
    userInput: SimulationDemand): Promise<Simulation> {
    const source = Axios.CancelToken.source();
    if (this.simulationRequests.length > 0) {
      this.simulationRequests.forEach(item => {
        item.cancel('Operation canceled by the user.');
      })
    }

    this.simulationRequests.push(source);
    const response = await this.simulationAxios.post<Simulation>(`${this.simulationPrefix(type)}backend/api/${this.getSimulationVersion(type)}persistent/simulation/${id}`, this.prepareUserInput(userInput), {cancelToken: source.token,});
    this.simulationRequests.shift();
    return response.data
  };

  public async duplicateLoan(id: string, type: ProductType, params: AdditionalRequests): Promise<AdditionalRequests> {
    const response = await this.simulationAxios.post(`${this.simulationPrefix(type)}backend/api/simulation/loan/${id}/duplicate`, params);
    return response.data;
  }

  public async removeDuplication(id: string, type: ProductType, params: AdditionalRequests): Promise<AdditionalRequests> {
    const response = await this.simulationAxios.post(`${this.simulationPrefix(type)}backend/api/simulation/loan/${id}/removeDuplicate`, params);
    return response.data;
  }

  public async isAvailable(id: string, type: ProductType, userInput: SimulationDemand): Promise<boolean> {
    const response = await this.simulationAxios.post<{
      isAvailable: boolean
    }>(`${this.simulationPrefix(type)}backend/api/simulation/loan/${id}/isAvailable`, this.prepareUserInput(userInput));
    return response.data.isAvailable;
  }

  public async addOffersToDeal(dealId: number, simulationId: string, userInput: SimulationDemand, offers: Offer[]) {
    const response = await this.dealsAxios.post<any>(`deals/${dealId}/applications`, {
      simulationInput: this.prepareUserInput(userInput),
      simulationId,
      offers,
    });
    return response.data;
  }

  public async generatePdf(url: string, filename: string) {
    const response = await this.pdfAxios.post<{ url: string }>(`${processProxy.VUE_APP_SIMULATION_URL}pdf`, {
      url,
      filename,
    });
    return response.data;
  }

  public async pinOffer(productType: ProductType, simulationId: string, offerId: string) {
    await this.simulationAxios.put(`${this.simulationPrefix(productType)}backend/api/simulation/${simulationId}/pinned-offers/${offerId}`);
  }

  public async unpinOffer(productType: ProductType, simulationId: string, offerId: string) {
    await this.simulationAxios.delete(`${this.simulationPrefix(productType)}backend/api/simulation/${simulationId}/pinned-offers/${offerId}`);
  }

  public async selectOffer(productType: ProductType, simulationId: string, offerId: string) {
    await this.simulationAxios.put(`${this.simulationPrefix(productType)}backend/api/simulation/${simulationId}/selected-offers/${offerId}`);
  }

  public async unselectOffer(productType: ProductType, simulationId: string, offerId: string) {
    await this.simulationAxios.delete(`${this.simulationPrefix(productType)}backend/api/simulation/${simulationId}/selected-offers/${offerId}`);
  }

  public async getSchedule(scheduleParams: ScheduleParams): Promise<Schedule> {
    const response = await this.simulationAxios.post<Schedule>(`${processProxy.VUE_APP_SIMULATION_URL}backend/api/schedule`, scheduleParams);
    return response.data;
  }

  public async getHomeStartAssumptions(homeStartProgramData: HomeStartProgramInput & {loanAmount: SimulationDemand['loanAmount']}): Promise<HomeStartProgram> {
    const response = await this.simulationAxios.post<HomeStartProgram>(`${processProxy.VUE_APP_SIMULATION_URL}backend/api/home-start-assumptions`, homeStartProgramData);
    return response.data;
  }

  public async getCashSchedule(scheduleParams: ScheduleParams): Promise<Schedule> {
    const response = await this.simulationAxios.post<Schedule>(`${processProxy.VUE_APP_SIMULATION_URL}cash-backend/api/schedule`, scheduleParams);
    return response.data;
  }
}

export default new SimulationApi();
